import { getPulseClient } from '../client/client';
import { ResourceDefinition } from './types';
import { ResolverState } from '../resolvers/definition/resolverState';
import { FunctionalState, NoParams } from '../resolvers/definition/resolver';
import { ResolverHttpClient } from '../resolvers/definition/httpClient';
import {
  WatchCallback,
  WatchStatesCallback,
  ResourcePromise,
  PromiseMode,
  ResourceInstanceState,
  SuspendedState,
} from '../client/types';
import {
  BaseOptions,
  SkipToken,
  ResourceWatchDescriptor,
  ResourceLoadDescriptor,
  normalizeResourceOptions,
} from './descriptors';

type GetStateOptions = {
  instanceKey?: string;
  materialize?: boolean;
};

type WatchOptions = {
  instanceKey?: string;
  pollInterval?: number;
  pollGroup?: string;
  httpClient?: ResolverHttpClient;
  keepAliveWhenHidden?: boolean;
};

type InvalidateOptions = {
  instanceKey?: string;
};

type RefetchOptions = {
  instanceKey?: string;
  httpClient?: ResolverHttpClient;
};

type SetStateOptions = {
  instanceKey?: string;
  optimistic?: boolean;
};

type GetPromiseOptions<TStale extends boolean = false> = {
  instanceKey?: string;
  resolveWithStale?: TStale;
  httpClient?: ResolverHttpClient;
};

type ParamsMethods<TParams, TData, TError extends Error = Error> = {
  getState(
    params: TParams,
    options?: GetStateOptions
  ): ResolverState<TData, TError> | undefined;

  watch(
    params: TParams,
    callback?: WatchCallback<TData, TError>,
    options?: WatchOptions
  ): () => void;

  invalidate(params: TParams, options?: InvalidateOptions): void;

  invalidateAll(): void;

  refetch(
    params: TParams,
    options?: RefetchOptions
  ): Promise<SuspendedState<TData, TError>>;

  retain(params: TParams, duration?: number): void;

  getStates(): ResourceInstanceState<TParams, TData, TError>[];

  watchStates(
    callback: WatchStatesCallback<TParams, TData, TError>
  ): () => void;

  setState(
    params: TParams,
    state?: FunctionalState<TData, TError>,
    options?: SetStateOptions
  ): void;

  getPromise<TStale extends boolean = false>(
    params: TParams,
    options?: GetPromiseOptions<TStale>
  ): ResourcePromise<TData, TError, TStale extends true ? 'stale' : 'fresh'>;

  toWatch(
    params: TParams | SkipToken,
    options?: BaseOptions
  ): ResourceWatchDescriptor<TParams, TData, TError>;

  toLoad<TMode extends PromiseMode = 'fresh'>(
    params: TParams | SkipToken,
    options?: BaseOptions & { mode?: TMode }
  ): ResourceLoadDescriptor<TMode, TParams, TData, TError>;
};

type NoParamsMethods<TData, TError extends Error = Error> = {
  getState(options?: GetStateOptions): ResolverState<TData, TError> | undefined;

  watch(
    callback?: WatchCallback<TData, TError>,
    options?: WatchOptions
  ): () => void;

  invalidate(options?: InvalidateOptions): void;

  invalidateAll(): void;

  refetch(options?: RefetchOptions): Promise<SuspendedState<TData, TError>>;

  retain(duration?: number): void;

  getStates(): ResourceInstanceState<NoParams, TData, TError>[];

  watchStates(
    callback: WatchStatesCallback<NoParams, TData, TError>
  ): () => void;

  setState(
    state?: FunctionalState<TData, TError>,
    options?: SetStateOptions
  ): void;

  getPromise<TStale extends boolean = false>(
    options?: GetPromiseOptions<TStale>
  ): ResourcePromise<TData, TError, TStale extends true ? 'stale' : 'fresh'>;

  toWatch(
    options?: BaseOptions
  ): ResourceWatchDescriptor<NoParams, TData, TError>;

  toLoad<TMode extends PromiseMode = 'fresh'>(
    options?: BaseOptions & { mode?: TMode }
  ): ResourceLoadDescriptor<TMode, NoParams, TData, TError>;
};

export type ResourceMethods<TParams, TData, TError extends Error = Error> = [
  TParams,
] extends [NoParams]
  ? NoParamsMethods<TData, TError>
  : ParamsMethods<TParams, TData, TError>;

export type Resource<
  TParams = any,
  TData = any,
  TError extends Error = Error,
> = ResourceDefinition<TParams, TData, TError> &
  ResourceMethods<TParams, TData, TError>;

export const createResourceMethods = <
  TParams,
  TData,
  TError extends Error = Error,
>(
  resource: ResourceDefinition<TParams, TData, TError>
): ResourceMethods<TParams, TData, TError> => {
  const isNoParams = !resource.args || resource.args.length === 0;

  if (isNoParams) {
    const empty = {} as TParams;
    const noParamsMethods: NoParamsMethods<TData, TError> = {
      getState: (options) =>
        getPulseClient().getState({ resource, params: empty, ...options }),
      watch: (callback, options) =>
        getPulseClient().watch(
          { resource, params: empty, ...options },
          callback
        ),
      invalidate: (options) =>
        getPulseClient().invalidate({ resource, params: empty, ...options }),
      invalidateAll: () => getPulseClient().invalidateAll({ resource }),
      refetch: (options) =>
        getPulseClient().refetch({ resource, params: empty, ...options }),
      retain: (duration) =>
        getPulseClient().retain({ resource, params: empty, duration }),
      getStates: () =>
        getPulseClient().getStates({
          resource: resource as ResourceDefinition<NoParams, TData, TError>,
        }),
      watchStates: (callback) =>
        getPulseClient().watchStates(
          {
            resource: resource as ResourceDefinition<NoParams, TData, TError>,
          },
          callback
        ),
      setState: (state, options) =>
        getPulseClient().setState({
          resource,
          params: empty,
          state,
          ...options,
        }),
      getPromise: (options) =>
        getPulseClient().getPromise({ resource, params: empty, ...options }),
      toWatch: (options) => ({
        resource: resource as ResourceDefinition<NoParams, TData, TError>,
        ...normalizeResourceOptions(options),
      }),
      toLoad: (options) => ({
        resource: resource as ResourceDefinition<NoParams, TData, TError>,
        ...normalizeResourceOptions(options),
        mode: (options?.mode ?? 'fresh') as any,
      }),
    };

    return noParamsMethods as ResourceMethods<TParams, TData, TError>;
  }

  const paramsMethods: ParamsMethods<TParams, TData, TError> = {
    getState: (params, options) =>
      getPulseClient().getState({ resource, params, ...options }),
    watch: (params, callback, options) =>
      getPulseClient().watch({ resource, params, ...options }, callback),
    invalidate: (params, options) =>
      getPulseClient().invalidate({ resource, params, ...options }),
    invalidateAll: () => getPulseClient().invalidateAll({ resource }),
    refetch: (params, options) =>
      getPulseClient().refetch({ resource, params, ...options }),
    retain: (params, duration) =>
      getPulseClient().retain({ resource, params, duration }),
    getStates: () => getPulseClient().getStates({ resource }),
    watchStates: (callback) =>
      getPulseClient().watchStates({ resource }, callback),
    setState: (params, state, options) =>
      getPulseClient().setState({ resource, params, state, ...options }),
    getPromise: (params, options) =>
      getPulseClient().getPromise({ resource, params, ...options }),
    toWatch: (params, options) => ({
      resource,
      ...normalizeResourceOptions({ params, ...options }),
    }),
    toLoad: (params, options) => ({
      resource,
      ...normalizeResourceOptions({ params, ...options }),
      mode: (options?.mode ?? 'fresh') as any,
    }),
  };

  return paramsMethods as ResourceMethods<TParams, TData, TError>;
};
